import React, { useState, useEffect, ChangeEvent } from "react";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import Input from "shared/Input/Input";
import useFetch from "hooks/useFetch";
import ErrorModal from "components/Modal/errorModal";
import SuccessModal from "components/Modal/successModal";
import config from "config.json";
import Label from "components/Label/Label";

// Definir el tipo de datos para el formulario, incluyendo la propiedad 'mail'
interface FormData {
  token: string;
  password: string;
  repeat_password: string;
  mail?: string;
}

const ForgotPass: React.FC = () => {
  const url = config.backend_url;

  // Estado para los datos del formulario
  const [formData, setFormData] = useState<FormData>({
    token: '',
    password: '',
    repeat_password: ''
  });

  const [errorMessage, setErrorMessage] = useState<string>("");
  const [validationError, setValidationError] = useState<string>("");

  // Estado para manejar el estado de la solicitud (idle, loading, success, error)
  const [requestStatus, setRequestStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  // Estado para controlar la visibilidad de las contraseñas
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const { data, error, isLoading, fetchData } = useFetch(
    'POST',
    `${url}/authe/set_pass`,
    formData,
    { 'Content-Type': 'application/json' }
  );

  // Validación del formulario
  const validateForm = (): boolean => {
    const { token, password, repeat_password } = formData;

    // Validar si los campos están vacíos
    if (!token || !password || !repeat_password) {
      setValidationError("Todos los campos son obligatorios.");
      return false;
    }

    // Validar si las contraseñas coinciden
    if (password !== repeat_password) {
      setValidationError("Las contraseñas no coinciden.");
      return false;
    }

    return true; // Si todo está correcto, devolver true
  };

  // Manejo del envío del formulario
  const handleSendVerification = (): void => {
    // Resetear los estados antes de cualquier nueva solicitud
    setRequestStatus('loading');  // Establecemos el estado a 'loading'
    setErrorMessage("");
    setValidationError("");

    // Validaciones del formulario
    if (!validateForm()) {
      setRequestStatus('idle');
      return; // No enviamos la solicitud si hay errores de validación
    }

    // Actualizar `formData` con la propiedad `mail` antes de llamar a `fetchData`
    setFormData((prevFormData) => ({
      ...prevFormData,
      mail: sessionStorage.getItem('recover-mail') || ''
    }));

    fetchData();
  };

  // Manejo de la respuesta y el error después de la llamada a fetchData
  useEffect(() => {
    // Si hay datos y la solicitud fue exitosa
    if (data && (data as { code: string }).code === '200') {
      setRequestStatus('success'); // Establecemos estado a 'success' si la respuesta es exitosa
      setErrorMessage("");  // Limpiar cualquier error previo
      sessionStorage.removeItem('recover-mail');
    } else if (error) {
      // Si ocurre un error, mostrar el modal de error
      setRequestStatus('error'); // Establecemos estado a 'error' si hay un error
      setErrorMessage("No se pudo guardar la nueva contraseña. Intente de nuevo más tarde.");
    }
  }, [data, error]);

  // Manejo del cambio de los inputs
  const handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = event.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
      mail: sessionStorage.getItem('recover-mail') || ''
    }));

    // Limpiar error de validación mientras el usuario escribe
    setValidationError("");
  };

  // Alternar visibilidad de la contraseña
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleCloseSuccessModal = (): void => {
    setRequestStatus('idle');  // Volver al estado inicial después de cerrar el modal de éxito
    window.location.href = '/login';  // Redirigir al login
  };

  return (
    <div className="space-y-6 sm:space-y-8 pt-5 pb-12 flex justify-center pl-10 pr-10">
      <SuccessModal
        isModalOpen={requestStatus === 'success'} // Mostrar modal solo si la solicitud fue exitosa
        handleModalClose={handleCloseSuccessModal}
        title="Nueva contraseña generada"
        message="Ya puedes iniciar sesión con tu nueva contraseña"
      />
      <ErrorModal
        isModalOpen={requestStatus === 'error'} // Mostrar modal solo si la solicitud falló
        handleModalClose={() => setRequestStatus('idle')} // Cerrar modal y volver al estado inicial
        message={errorMessage}
      />
      <div className="space-y-6 sm:space-y-8">
        <h2 className="text-3xl font-semibold text-center">Recuperar contraseña</h2>

        <div className="max-w-xl space-y-6">
          <div>
            <Label>Código de verificación</Label>
            <Input
              name="token"
              type="number"
              required
              className="mt-1.5"
              onChange={handleInputChange}
            />
          </div>
          <div>
            <Label>Nueva contraseña</Label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"} // Mostrar u ocultar la contraseña
                name="password"
                required
                className="mt-1.5"
                onChange={handleInputChange}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 px-4 text-gray-600"
                onClick={togglePasswordVisibility}
              >
                {showPassword ? "Ocultar" : "Mostrar"}
              </button>
            </div>
          </div>
          <div>
            <Label>Repetir contraseña</Label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"} // Mostrar u ocultar la contraseña
                name="repeat_password"
                required
                className="mt-1.5"
                onChange={handleInputChange}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 px-4 text-gray-600"
                onClick={togglePasswordVisibility}
              >
                {showPassword ? "Ocultar" : "Mostrar"}
              </button>
            </div>
          </div>
          {/* Mostrar error de validación sin modal */}
          {validationError && (
            <div className="text-red-500 text-center text-sm">{validationError}</div>
          )}
          <div className="pt-2 flex justify-center">
            <ButtonPrimary onClick={handleSendVerification} disabled={isLoading || requestStatus === 'loading'}>
              Guardar
            </ButtonPrimary>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPass;